import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2, CheckCircle, AlertTriangle, BookOpen, Heart, Shield, Users, Baby, Eye, Brain, Target, Play, RotateCcw, Handshake, MessageCircle, ShieldCheck, FileText, Star, UserCheck, Ear } from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'tp-iepe-bloc3-progress';

const TPIEPEBloc3Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showResume, setShowResume] = useState(false);
  const [savedSlide, setSavedSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const idx = parseInt(saved, 10);
        if (idx > 0 && idx < 55) { setSavedSlide(idx); setShowResume(true); }
      }
    } catch {}
  }, []);

  const updateSlide = useCallback((idx) => {
    setCurrentSlide(idx);
    try { localStorage.setItem(STORAGE_KEY, String(idx)); } catch {}
  }, []);

  const T = {
    primary: '#2563EB',
    secondary: '#3B82F6',
    gradient: 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 50%, #60A5FA 100%)',
    sectionGradient: 'linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)',
    light: '#EFF6FF',
    accent: '#1E40AF'
  };
  const P = T.primary;

  const slides = [
    // ═══════════════ SLIDE 1 — TITRE ═══════════════
    {
      type: 'title',
      title: "Bloc 3",
      subtitle: "Accompagner les parents ou l\u2019entourage des jeunes enfants",
      narration: "Bienvenue dans le Bloc 3 du Titre Professionnel Intervenant Éducatif Petite Enfance. Ce bloc porte sur l\u2019accompagnement des parents et de l\u2019entourage des jeunes enfants. Le professionnel s\u2019inscrit dans une logique systémique : l\u2019enfant ne peut être compris indépendamment de son environnement familial."
    },
    // ═══════════════ SLIDE 2 — SOMMAIRE ═══════════════
    {
      type: 'sommaire',
      title: "Sommaire du Bloc 3",
      sections: [
        { num: "Intro", title: "Introduction générale", items: ["Approche systémique", "Co-éducation et attachement", "Attendus du jury"], color: T.primary, targetSlide: 2 },
        { num: "1", title: "Accueillir et construire l\u2019alliance", items: ["L\u2019accueil comme moment fondateur", "Enjeux psychologiques", "Posture de Carl Rogers"], color: "#E75480", targetSlide: 12 },
        { num: "2", title: "Accompagnement à la parentalité", items: ["Concept de parentalité (Houzel)", "Posture de soutien", "Outils d\u2019accompagnement"], color: "#D97706", targetSlide: 22 },
        { num: "3", title: "Prévention précoce et inclusion", items: ["Repérage des vulnérabilités", "Posture professionnelle", "Adaptation et inclusion"], color: "#059669", targetSlide: 32 },
        { num: "4", title: "Communication professionnelle", items: ["Types de communication", "CNV", "Gestion des conflits"], color: "#8B5CF6", targetSlide: 41 }
      ],
      narration: "Voici le sommaire du Bloc 3. Nous commencerons par l\u2019introduction générale sur l\u2019approche systémique et la co-éducation. Puis le chapitre 1 sur l\u2019accueil et l\u2019alliance éducative. Le chapitre 2 sur l\u2019accompagnement à la parentalité. Le chapitre 3 sur la prévention précoce et l\u2019inclusion. Et enfin le chapitre 4 sur la communication professionnelle."
    },

    // ═══════════════ INTRODUCTION GÉNÉRALE ═══════════════
    {
      type: 'section',
      title: "INTRODUCTION GÉNÉRALE",
      subtitle: "L\u2019enfant dans son environnement familial",
      chapterIcon: "book",
      chapterNum: "Intro",
      chapterColor: T.primary,
      narration: "Dans ce bloc, le professionnel s\u2019inscrit dans une logique systémique. L\u2019enfant ne peut être compris indépendamment de son environnement familial. Le professionnel agit dans une co-construction éducative avec les parents."
    },
    // SLIDE 4 — Approche systémique
    {
      type: 'content',
      title: "L\u2019approche écologique de Bronfenbrenner",
      points: [
        "Le développement de l\u2019enfant est influencé par plusieurs systèmes",
        "La famille (microsystème) : premier cercle d\u2019influence",
        "La structure d\u2019accueil : second environnement clé",
        "Les interactions entre ces milieux : cohérence éducative",
        "Le professionnel agit dans une co-construction éducative avec les parents"
      ],
      highlight: "L\u2019enfant ne peut être compris indépendamment de son environnement familial. Chaque action du professionnel doit s\u2019inscrire dans cette vision globale.",
      narration: "Selon l\u2019approche écologique de Bronfenbrenner, le développement de l\u2019enfant est influencé par la famille, la structure d\u2019accueil et les interactions entre ces milieux. Le professionnel agit dans une co-construction éducative avec les parents."
    },
    // SLIDE 5 — Idée centrale
    {
      type: 'comparison',
      title: "L\u2019idée centrale du Bloc 3",
      left: { title: "Le parent est\u2026", items: ["Le premier éducateur de l\u2019enfant", "Celui qui connaît le mieux son enfant", "Un partenaire essentiel", "Porteur d\u2019une histoire et de valeurs"] },
      right: { title: "Le professionnel\u2026", items: ["Soutient et accompagne", "Complète sans remplacer", "Apporte un regard professionnel", "Co-construit avec la famille"] },
      narration: "L\u2019idée centrale : le parent est le premier éducateur de l\u2019enfant. Le professionnel soutient, accompagne et complète, sans jamais remplacer le parent."
    },
    // SLIDE 6 — Co-éducation
    {
      type: 'content',
      title: "La co-éducation",
      points: [
        "Partenariat entre parents et professionnels",
        "Basée sur la confiance mutuelle",
        "Communication régulière et transparente",
        "Complémentarité des rôles : chacun apporte sa contribution",
        "Objectif commun : le bien-être et le développement de l\u2019enfant"
      ],
      highlight: "La co-éducation n\u2019est pas un partage de pouvoir, c\u2019est une alliance dans l\u2019intérêt de l\u2019enfant. Le parent et le professionnel ont chacun leur place et leur légitimité.",
      narration: "La co-éducation est un partenariat entre parents et professionnels basé sur la confiance, la communication et la complémentarité. L\u2019objectif commun est le bien-être et le développement de l\u2019enfant."
    },
    // SLIDE 7 — Attachement
    {
      type: 'multi',
      title: "L\u2019attachement (Bowlby / Ainsworth)",
      sections: [
        { title: "Principes fondamentaux", items: ["L\u2019enfant a besoin de figures d\u2019attachement stables", "La séparation est un moment sensible qui active le système d\u2019attachement", "La sécurité affective influence la capacité d\u2019exploration", "L\u2019attachement se construit dans les premières années de vie"] },
        { title: "Types d\u2019attachement", items: ["Attachement sécure : confiance, exploration sereine", "Attachement insécure-évitant : peu de réaction à la séparation", "Attachement insécure-ambivalent : détresse intense, difficulté d\u2019apaisement", "Attachement désorganisé : comportements contradictoires"] },
        { title: "Rôle du professionnel", items: ["Devenir une figure d\u2019attachement secondaire sécurisante", "Faciliter la transition entre la figure principale (parent) et la structure", "Observer les comportements d\u2019attachement pour comprendre l\u2019enfant", "Sécuriser l\u2019enfant par la prévisibilité et la constance"] }
      ],
      narration: "Selon Bowlby et Ainsworth, l\u2019enfant a besoin de figures stables. La séparation est un moment sensible. La sécurité affective influence l\u2019exploration. Le professionnel devient une figure d\u2019attachement secondaire sécurisante."
    },
    // SLIDE 8 — Parentalité
    {
      type: 'content',
      title: "La parentalité : une fonction complexe",
      points: [
        "La parentalité n\u2019est PAS une compétence à évaluer ou à noter",
        "C\u2019est une fonction complexe influencée par :",
        "L\u2019histoire personnelle du parent",
        "Le contexte social et économique",
        "Les émotions liées à la naissance et à l\u2019éducation",
        "Les représentations éducatives héritées ou choisies"
      ],
      highlight: "Le professionnel n\u2019est pas là pour juger la parentalité. Il est là pour la soutenir, l\u2019accompagner et la valoriser.",
      narration: "La parentalité n\u2019est pas une compétence à évaluer. C\u2019est une fonction complexe influencée par l\u2019histoire personnelle, le contexte social, les émotions et les représentations éducatives. Le professionnel soutient sans juger."
    },
    // SLIDE 9 — Soutien à la parentalité
    {
      type: 'content',
      title: "Le soutien à la parentalité",
      points: [
        "Selon les politiques publiques : accompagner sans juger ni remplacer",
        "Reconnaître les compétences parentales existantes",
        "Proposer des ressources adaptées aux besoins de chaque famille",
        "Créer des espaces de parole et d\u2019échange",
        "Orienter vers des partenaires spécialisés si nécessaire"
      ],
      narration: "Le soutien à la parentalité, selon les politiques publiques, consiste à accompagner sans juger ni remplacer. Il s\u2019agit de reconnaître les compétences parentales, proposer des ressources adaptées et créer des espaces d\u2019échange."
    },
    // SLIDE 10 — Démarche professionnelle
    {
      type: 'method',
      title: "La démarche professionnelle du Bloc 3",
      steps: [
        { label: "Accueillir", desc: "Créer un espace de confiance dès le premier contact, être disponible et ouvert", icon: "heart" },
        { label: "Écouter", desc: "Adopter une écoute active, sans jugement, reformuler pour montrer la compréhension", icon: "eye" },
        { label: "Comprendre", desc: "Saisir le contexte familial, les émotions, les attentes et les besoins du parent", icon: "brain" },
        { label: "Adapter", desc: "Ajuster sa posture, sa communication et ses propositions à chaque famille", icon: "target" },
        { label: "Co-construire", desc: "Élaborer ensemble un accompagnement dans l\u2019intérêt de l\u2019enfant", icon: "hand" }
      ],
      quote: "\u00ab J\u2019accueille le parent dans une posture bienveillante, je prends en compte sa singularité et ses besoins, puis je co-construis avec lui un accompagnement adapté dans l\u2019intérêt de l\u2019enfant. \u00bb",
      narration: "La démarche professionnelle du Bloc 3 comporte cinq étapes : accueillir, écouter, comprendre, adapter et co-construire. Chaque étape vise à créer une alliance éducative dans l\u2019intérêt de l\u2019enfant."
    },
    // SLIDE 11 — Attendu du jury
    {
      type: 'grid',
      title: "Ce que le jury attend dans le Bloc 3",
      items: [
        { num: "1", label: "Posture non jugeante envers les parents et les familles" },
        { num: "2", label: "Communication adaptée à chaque interlocuteur et à chaque situation" },
        { num: "3", label: "Capacité à construire une alliance éducative avec les familles" },
        { num: "4", label: "Respect de la singularité familiale : culture, valeurs, histoire" },
        { num: "5", label: "Position professionnelle claire : ni ami, ni expert qui impose, ni juge" }
      ],
      narration: "Le jury attend : une posture non jugeante, une communication adaptée, une alliance éducative, le respect de la singularité familiale et une position professionnelle claire."
    },

    // ═══════════════ CHAPITRE 1 — ACCUEIL ET ALLIANCE ═══════════════
    {
      type: 'section',
      title: "CHAPITRE 1",
      subtitle: "Accueillir les parents et construire une alliance éducative",
      chapterIcon: "heart",
      chapterNum: "01",
      chapterColor: "#E75480",
      narration: "Chapitre 1 : Accueillir les parents et construire une alliance éducative. L\u2019objectif est de créer une relation de confiance durable avec les familles, dès le premier accueil."
    },
    // SLIDE 13 — L'accueil : moment fondateur
    {
      type: 'content',
      title: "L\u2019accueil : un moment fondateur",
      points: [
        "L\u2019accueil correspond à une séparation parent-enfant",
        "C\u2019est un moment de vulnérabilité pour les deux",
        "Selon la théorie de l\u2019attachement : activation du système d\u2019attachement",
        "L\u2019enfant manifeste sa détresse par des pleurs, un accrochage, un repli",
        "Le parent peut ressentir culpabilité, inquiétude, ambivalence"
      ],
      highlight: "L\u2019accueil du matin n\u2019est jamais anodin. C\u2019est un moment professionnel qui demande une disponibilité totale et une attention fine aux signaux de l\u2019enfant ET du parent.",
      narration: "L\u2019accueil correspond à une séparation parent-enfant. Selon la théorie de l\u2019attachement, c\u2019est un moment de vulnérabilité qui active le système d\u2019attachement. Le professionnel doit être disponible et attentif aux signaux de l\u2019enfant et du parent."
    },
    // SLIDE 14 — Enjeux psychologiques
    {
      type: 'comparison',
      title: "Les enjeux psychologiques de la séparation",
      left: { title: "Pour l\u2019enfant", items: ["Peur de la séparation et de l\u2019abandon", "Besoin de continuité et de prévisibilité", "Recherche de la figure d\u2019attachement", "Manifestations : pleurs, agrippement, retrait", "Besoin d\u2019être sécurisé et contenu"] },
      right: { title: "Pour le parent", items: ["Culpabilité de laisser son enfant", "Inquiétude sur la qualité de l\u2019accueil", "Besoin d\u2019être rassuré et reconnu", "Besoin de confiance envers le professionnel", "Besoin de sentir que son enfant est bien"] },
      narration: "La séparation mobilise des enjeux pour l\u2019enfant, qui ressent la peur et le besoin de sécurité, et pour le parent, qui vit la culpabilité et l\u2019inquiétude. Le professionnel doit sécuriser les deux."
    },
    // SLIDE 15 — Carl Rogers
    {
      type: 'grid',
      title: "La posture professionnelle inspirée de Carl Rogers",
      items: [
        { num: "1", label: "Écoute active : être pleinement présent, attentif aux mots et aux émotions" },
        { num: "2", label: "Empathie : se mettre à la place de l\u2019autre sans se confondre avec lui" },
        { num: "3", label: "Congruence : être authentique et cohérent entre ce que l\u2019on ressent et ce que l\u2019on montre" },
        { num: "4", label: "Regard positif inconditionnel : accueillir sans juger, sans condition" }
      ],
      narration: "La posture professionnelle s\u2019inspire de Carl Rogers avec quatre dimensions : l\u2019écoute active, l\u2019empathie, la congruence et le regard positif inconditionnel."
    },
    // SLIDE 16 — Outils de l'accueil
    {
      type: 'multi',
      title: "Les outils de l\u2019accueil",
      sections: [
        { title: "Verbalisation", items: ["Nommer ce qui se passe : \u00ab Tu es triste, maman va partir \u00bb", "Donner des repères temporels : \u00ab Elle revient après le goûter \u00bb", "Reconnaître l\u2019émotion sans la minimiser"] },
        { title: "Regard et posture", items: ["Se mettre à hauteur de l\u2019enfant et du parent", "Regard bienveillant et disponible", "Posture d\u2019ouverture corporelle (pas de bras croisés)"] },
        { title: "Disponibilité réelle", items: ["Ne pas être absorbé par d\u2019autres tâches pendant l\u2019accueil", "Prendre le temps nécessaire", "Montrer au parent qu\u2019il est vu et entendu"] }
      ],
      narration: "Les outils de l\u2019accueil sont la verbalisation pour nommer les émotions, le regard et la posture corporelle pour montrer la disponibilité, et la disponibilité réelle pour prendre le temps nécessaire."
    },
    // SLIDE 17 — Alliance éducative
    {
      type: 'content',
      title: "L\u2019alliance éducative",
      points: [
        "Concept clé du Bloc 3 : relation de coopération dans l\u2019intérêt de l\u2019enfant",
        "Elle ne se décrète pas, elle se construit progressivement",
        "Elle repose sur la reconnaissance du parent comme acteur central",
        "Elle nécessite une confiance réciproque",
        "Elle passe par une communication régulière, transparente et respectueuse"
      ],
      highlight: "L\u2019alliance éducative n\u2019est pas une soumission au parent ni une imposition du professionnel. C\u2019est un espace de dialogue où chacun apporte sa contribution dans l\u2019intérêt de l\u2019enfant.",
      narration: "L\u2019alliance éducative est une relation de coopération dans l\u2019intérêt de l\u2019enfant. Elle se construit progressivement, repose sur la reconnaissance du parent et nécessite une confiance réciproque."
    },
    // SLIDE 18 — Clés de l'alliance
    {
      type: 'grid',
      title: "Les clés de l\u2019alliance éducative",
      items: [
        { num: "1", label: "Reconnaissance : valoriser le parent dans son rôle et ses compétences" },
        { num: "2", label: "Confiance : créer un climat de sécurité où le parent ose s\u2019exprimer" },
        { num: "3", label: "Communication : échanger régulièrement sur le quotidien de l\u2019enfant" },
        { num: "4", label: "Cohérence : rechercher une continuité entre la maison et la structure" },
        { num: "5", label: "Respect : accepter les différences culturelles et éducatives" }
      ],
      narration: "Les cinq clés de l\u2019alliance éducative sont : la reconnaissance, la confiance, la communication, la cohérence et le respect."
    },
    // SLIDE 19 — Exemple accueil
    {
      type: 'example',
      title: "Exemple — Parent anxieux le matin",
      situation: "Un parent arrive visiblement stressé. Il s\u2019attarde, demande plusieurs fois si tout ira bien, a du mal à partir.",
      observation: ["Posture tendue, regard inquiet", "Questions répétitives sur l\u2019organisation de la journée", "L\u2019enfant s\u2019accroche au parent", "Le parent hésite à partir"],
      analyse: ["Insécurité liée à la séparation", "Difficulté de confiance dans la structure ou le professionnel", "Possible culpabilité de confier son enfant", "L\u2019anxiété du parent amplifie celle de l\u2019enfant"],
      action: ["Accueillir le parent avec chaleur et disponibilité", "Reformuler ses inquiétudes : \u00ab Je vois que vous êtes inquiet ce matin \u00bb", "Donner des informations concrètes et rassurantes", "Proposer un rituel de séparation (ex : dire au revoir par la fenêtre)", "Donner des nouvelles dans la journée si possible"],
      formulation: "\u00ab Je comprends que la séparation est difficile ce matin. Je vais veiller sur votre enfant. N\u2019hésitez pas à appeler si vous avez besoin. \u00bb",
      narration: "Exemple : un parent anxieux le matin. Analyse : insécurité et difficulté de séparation. Réponse : accueillir avec chaleur, reformuler les inquiétudes, rassurer concrètement et proposer un rituel de séparation."
    },
    // SLIDE 20 — Erreurs accueil
    {
      type: 'posture',
      title: "Erreurs fréquentes lors de l\u2019accueil",
      wrong: [
        "Minimiser l\u2019émotion du parent : \u00ab Ça va aller, ne vous inquiétez pas \u00bb",
        "Prendre l\u2019enfant des bras du parent brusquement",
        "Être occupé à autre chose pendant l\u2019accueil",
        "Comparer avec d\u2019autres enfants : \u00ab Les autres ne pleurent pas \u00bb",
        "Expédier l\u2019accueil par manque de temps"
      ],
      right: [
        "Reconnaître l\u2019émotion : \u00ab C\u2019est normal, la séparation est un moment délicat \u00bb",
        "Laisser le parent poser son enfant lui-même",
        "Être pleinement disponible pendant le temps de l\u2019accueil",
        "Individualiser : chaque famille a son propre rythme",
        "Prendre le temps nécessaire, même si c\u2019est plus long"
      ],
      narration: "Les erreurs fréquentes lors de l\u2019accueil : minimiser les émotions, prendre l\u2019enfant brusquement, être occupé, comparer ou expédier. Les bonnes pratiques : reconnaître, laisser le parent agir, être disponible, individualiser et prendre le temps."
    },
    // SLIDE 21 — Attendu jury Ch1
    {
      type: 'content',
      title: "Attendu du jury — Chapitre 1",
      points: [
        "Tu dois montrer ta compréhension des enjeux affectifs de la séparation",
        "Démontrer une posture contenante envers l\u2019enfant ET le parent",
        "Savoir expliquer pourquoi l\u2019accueil est un moment professionnel clé",
        "Utiliser le vocabulaire professionnel : attachement, sécurité affective, alliance éducative",
        "Montrer ta capacité à adapter ta posture selon chaque famille"
      ],
      narration: "Le jury attend que tu montres ta compréhension des enjeux affectifs, une posture contenante, l\u2019utilisation du vocabulaire professionnel et ta capacité d\u2019adaptation."
    },

    // ═══════════════ CHAPITRE 2 — PARENTALITÉ ═══════════════
    {
      type: 'section',
      title: "CHAPITRE 2",
      subtitle: "Accompagnement à la parentalité",
      chapterIcon: "users",
      chapterNum: "02",
      chapterColor: "#D97706",
      narration: "Chapitre 2 : Accompagnement à la parentalité. L\u2019objectif est de soutenir la fonction parentale sans intrusion, en reconnaissant le parent comme l\u2019acteur central de la vie de son enfant."
    },
    // SLIDE 23 — Concept de parentalité
    {
      type: 'multi',
      title: "Le concept de parentalité selon Didier Houzel",
      sections: [
        { title: "L\u2019exercice de la parentalité", items: ["Les droits et devoirs liés au statut de parent", "L\u2019autorité parentale", "La responsabilité juridique envers l\u2019enfant", "Le cadre légal de la filiation"] },
        { title: "L\u2019expérience de la parentalité", items: ["Le ressenti émotionnel d\u2019être parent", "Le vécu subjectif : joie, inquiétude, culpabilité", "L\u2019impact de l\u2019histoire personnelle du parent", "Les représentations intériorisées de ce qu\u2019est un \u00ab bon parent \u00bb"] },
        { title: "La pratique de la parentalité", items: ["Les actions concrètes du quotidien", "Les soins, l\u2019éducation, l\u2019organisation", "Les choix éducatifs", "L\u2019adaptation aux besoins de l\u2019enfant"] }
      ],
      narration: "Selon Didier Houzel, la parentalité comporte trois dimensions : l\u2019exercice avec les droits et devoirs, l\u2019expérience avec le ressenti émotionnel, et la pratique avec les actions concrètes du quotidien."
    },
    // SLIDE 24 — Posture du professionnel
    {
      type: 'content',
      title: "La posture du professionnel face à la parentalité",
      points: [
        "Posture de soutien et non d\u2019expertise dominante",
        "Le professionnel n\u2019est pas un \u00ab expert \u00bb qui sait mieux que le parent",
        "Il accompagne, il propose, il ouvre des pistes",
        "Il valorise ce que le parent fait de bien",
        "Il s\u2019adapte au rythme et aux capacités de chaque parent"
      ],
      highlight: "La posture de soutien repose sur l\u2019idée que le parent possède des ressources. Le rôle du professionnel est de les révéler et de les renforcer, pas de les remplacer.",
      narration: "Le professionnel adopte une posture de soutien, et non d\u2019expertise dominante. Il accompagne, propose, valorise ce que le parent fait de bien et s\u2019adapte au rythme de chaque parent."
    },
    // SLIDE 25 — Risques à éviter
    {
      type: 'posture',
      title: "Risques à éviter dans l\u2019accompagnement parental",
      wrong: [
        "Se positionner en expert qui impose ses vues",
        "Porter un jugement implicite : \u00ab Vous ne devriez pas faire comme ça \u00bb",
        "Comparer les familles entre elles",
        "Donner des leçons de parentalité",
        "Projeter ses propres valeurs éducatives sur la famille"
      ],
      right: [
        "Reconnaître le parent comme acteur principal",
        "Proposer sans imposer : \u00ab Certaines familles trouvent utile de\u2026 \u00bb",
        "Valoriser les réussites et les progrès",
        "Écouter avant de répondre",
        "Respecter les choix éducatifs dans le cadre du bien-être de l\u2019enfant"
      ],
      narration: "Les risques à éviter : se positionner en expert, juger, comparer, donner des leçons ou projeter ses valeurs. Les bonnes pratiques : reconnaître le parent, proposer sans imposer, valoriser, écouter et respecter."
    },
    // SLIDE 26 — Dolto
    {
      type: 'content',
      title: "L\u2019approche de Françoise Dolto",
      points: [
        "Le parent doit être reconnu dans sa place de parent",
        "La parole est essentielle : parler avec le parent, pas sur le parent",
        "L\u2019enfant comprend plus qu\u2019on ne le croit : la vérité dite avec des mots simples est toujours préférable",
        "Le respect de la personne du parent est un fondement de la relation",
        "Chaque famille a sa propre dynamique et sa propre logique"
      ],
      highlight: "Dolto nous rappelle que le parent n\u2019est pas un élève à éduquer. C\u2019est un partenaire à reconnaître dans sa complexité et sa singularité.",
      narration: "Selon Françoise Dolto, le parent doit être reconnu dans sa place. La parole est essentielle. Le respect de la personne du parent est un fondement de la relation professionnelle."
    },
    // SLIDE 27 — Outils d'accompagnement
    {
      type: 'grid',
      title: "Les outils d\u2019accompagnement à la parentalité",
      items: [
        { num: "1", label: "Entretiens individuels : échanges réguliers sur le développement de l\u2019enfant et les préoccupations du parent" },
        { num: "2", label: "Temps informels : échanges quotidiens lors de l\u2019accueil et du départ, moments de convivialité" },
        { num: "3", label: "Ateliers parents-enfants : activités partagées qui renforcent le lien et la confiance" },
        { num: "4", label: "Supports visuels : photos du quotidien, cahiers de liaison, panneaux d\u2019information" },
        { num: "5", label: "Réunions collectives : espaces de parole entre parents et professionnels" }
      ],
      narration: "Les outils d\u2019accompagnement incluent les entretiens individuels, les temps informels quotidiens, les ateliers parents-enfants, les supports visuels et les réunions collectives."
    },
    // SLIDE 28 — Écoute active
    {
      type: 'grid',
      title: "Les composantes de l\u2019écoute active",
      items: [
        { num: "1", label: "Reformulation : \u00ab Si je comprends bien, vous dites que\u2026 \u00bb" },
        { num: "2", label: "Questions ouvertes : \u00ab Comment ça se passe le soir ? \u00bb (plutôt que \u00ab Ça va ? \u00bb)" },
        { num: "3", label: "Absence de jugement : accueillir la parole sans corriger ni contredire" },
        { num: "4", label: "Validation émotionnelle : \u00ab C\u2019est normal de se sentir fatigué, c\u2019est exigeant \u00bb" },
        { num: "5", label: "Silence bienveillant : laisser le parent formuler sa pensée sans l\u2019interrompre" }
      ],
      narration: "L\u2019écoute active comprend la reformulation, les questions ouvertes, l\u2019absence de jugement, la validation émotionnelle et le silence bienveillant."
    },
    // SLIDE 29 — Exemple parentalité
    {
      type: 'example',
      title: "Exemple — Parent épuisé (sommeil de l\u2019enfant)",
      situation: "Un parent confie qu\u2019il est épuisé car son enfant de 14 mois ne dort toujours pas la nuit. Il se sent dépassé et coupable.",
      observation: ["Le parent a les traits tirés et semble fatigué", "Il parle de \u00ab culpabilité \u00bb et de \u00ab ne pas y arriver \u00bb", "Il compare avec d\u2019autres enfants qui dorment", "Il demande conseil au professionnel"],
      analyse: ["Épuisement physique et émotionnel", "Culpabilité liée aux représentations du \u00ab bon parent \u00bb", "Besoin d\u2019écoute et de déculpabilisation", "Besoin de pistes concrètes mais pas de leçons"],
      action: ["Écouter sans interrompre ni minimiser", "Valider l\u2019émotion : \u00ab C\u2019est épuisant, c\u2019est normal de se sentir dépassé \u00bb", "Déculpabiliser : \u00ab Chaque enfant a son propre rythme de sommeil \u00bb", "Proposer des pistes adaptées (rituels, environnement calme)", "Orienter vers un professionnel si nécessaire (pédiatre, consultante en sommeil)"],
      formulation: "\u00ab Je vous entends. C\u2019est vraiment épuisant les nuits sans sommeil. Chaque enfant a son rythme. Si vous le souhaitez, on peut réfléchir ensemble à quelques pistes. \u00bb",
      narration: "Exemple : un parent épuisé par le sommeil de son enfant. Réponse : écouter, valider l\u2019émotion, déculpabiliser, proposer des pistes adaptées et orienter si nécessaire. Jamais juger ni comparer."
    },
    // SLIDE 30 — Attendu jury Ch2
    {
      type: 'content',
      title: "Attendu du jury — Chapitre 2",
      points: [
        "Montrer que tu valorises les compétences parentales",
        "Démontrer une posture d\u2019accompagnement (pas d\u2019imposition)",
        "Connaître le concept de parentalité de Houzel (3 dimensions)",
        "Savoir utiliser l\u2019écoute active et la reformulation",
        "Proposer des pistes sans donner de leçons"
      ],
      narration: "Le jury attend que tu montres ta capacité à valoriser, accompagner, utiliser l\u2019écoute active et proposer sans imposer."
    },
    // SLIDE 31 — Transition
    {
      type: 'content',
      title: "De l\u2019accompagnement à la vigilance",
      points: [
        "Le Bloc 3 ne se limite pas à soutenir les familles \u00ab ordinaires \u00bb",
        "Le professionnel doit aussi repérer les situations de vulnérabilité",
        "Certains enfants ou certaines familles ont besoin d\u2019un accompagnement renforcé",
        "La prévention précoce est un enjeu majeur en petite enfance",
        "Le chapitre 3 aborde cette dimension essentielle"
      ],
      narration: "Le Bloc 3 ne se limite pas au soutien des familles ordinaires. Le professionnel doit aussi repérer les situations de vulnérabilité. La prévention précoce est un enjeu majeur en petite enfance."
    },

    // ═══════════════ CHAPITRE 3 — PRÉVENTION ET INCLUSION ═══════════════
    {
      type: 'section',
      title: "CHAPITRE 3",
      subtitle: "Prévention précoce et inclusion",
      chapterIcon: "shield",
      chapterNum: "03",
      chapterColor: "#059669",
      narration: "Chapitre 3 : Prévention précoce et inclusion. L\u2019objectif est de repérer et accompagner les situations de vulnérabilité, en s\u2019inscrivant dans une démarche de prévention et d\u2019inclusion."
    },
    // SLIDE 33 — Prévention précoce
    {
      type: 'content',
      title: "La prévention précoce : un concept central",
      points: [
        "Agir tôt pour prévenir les difficultés ultérieures",
        "Les premières années sont déterminantes pour le développement",
        "Le neurodéveloppement confirme l\u2019importance des expériences précoces",
        "Un repérage précoce permet une prise en charge adaptée",
        "La prévention n\u2019est pas la prédiction : on ne prédit pas l\u2019avenir d\u2019un enfant"
      ],
      highlight: "La prévention précoce repose sur une vigilance professionnelle : observer sans alarmer, repérer sans étiqueter, transmettre sans diagnostiquer.",
      narration: "La prévention précoce est un concept central en petite enfance. Les premières années sont déterminantes. Un repérage précoce permet une prise en charge adaptée. La prévention repose sur l\u2019observation, le repérage et la transmission, sans diagnostic."
    },
    // SLIDE 34 — Ce que tu repères
    {
      type: 'grid',
      title: "Ce que le professionnel peut repérer",
      items: [
        { num: "1", label: "Retard moteur : l\u2019enfant ne se retourne pas, ne s\u2019assoit pas, ne marche pas dans les délais habituels" },
        { num: "2", label: "Troubles relationnels : l\u2019enfant évite le regard, ne recherche pas le contact, est en retrait permanent" },
        { num: "3", label: "Isolement : l\u2019enfant ne joue pas avec les autres, reste seul, ne s\u2019intègre pas au groupe" },
        { num: "4", label: "Troubles alimentaires : refus de manger, vomissements, angoisse au moment du repas" },
        { num: "5", label: "Troubles du sommeil : difficultés d\u2019endormissement, réveils fréquents, cauchemars" },
        { num: "6", label: "Signes de maltraitance : traces inexpliquées, changement brutal de comportement, crainte de l\u2019adulte" }
      ],
      narration: "Le professionnel peut repérer : des retards moteurs, des troubles relationnels, de l\u2019isolement, des troubles alimentaires, des troubles du sommeil et des signes de maltraitance."
    },
    // SLIDE 35 — Approche théorique
    {
      type: 'multi',
      title: "Approche théorique de la prévention",
      sections: [
        { title: "Neurodéveloppement", items: ["Les premières années sont une période de plasticité cérébrale intense", "Les expériences précoces façonnent les connexions neuronales", "Un environnement sécurisant favorise un développement optimal", "Le stress toxique (maltraitance, négligence) peut altérer le développement"] },
        { title: "Attachement", items: ["La sécurité affective est la base du développement", "Un attachement insécure peut entraîner des difficultés relationnelles", "Le professionnel contribue à la sécurité affective de l\u2019enfant", "L\u2019observation des comportements d\u2019attachement est un outil de repérage"] }
      ],
      narration: "Le neurodéveloppement confirme l\u2019importance des premières années et de la plasticité cérébrale. Les théories de l\u2019attachement montrent que la sécurité affective est la base du développement."
    },
    // SLIDE 36 — Posture professionnelle prévention
    {
      type: 'method',
      title: "La posture professionnelle en prévention",
      steps: [
        { label: "Observer", desc: "Repérer les signaux faibles au quotidien, noter les faits objectifs", icon: "eye" },
        { label: "Alerter", desc: "Partager ses observations avec l\u2019équipe, ne pas rester seul avec ses inquiétudes", icon: "target" },
        { label: "Transmettre", desc: "Communiquer les observations aux partenaires compétents (PMI, médecin, psychologue)", icon: "hand" },
        { label: "Accompagner", desc: "Soutenir l\u2019enfant et la famille dans les démarches, adapter l\u2019accueil", icon: "heart" }
      ],
      quote: "Le professionnel observe et transmet, mais ne pose JAMAIS de diagnostic. Le diagnostic relève des professionnels de santé.",
      narration: "La posture professionnelle en prévention : observer les signaux faibles, alerter l\u2019équipe, transmettre aux partenaires compétents et accompagner l\u2019enfant et la famille. Le professionnel ne pose jamais de diagnostic."
    },
    // SLIDE 37 — Limites
    {
      type: 'posture',
      title: "Les limites du professionnel",
      wrong: [
        "Poser un diagnostic : \u00ab Cet enfant est autiste \u00bb",
        "Alarmer les parents sans fondement",
        "Décider seul d\u2019un signalement",
        "Interpréter au-delà des faits observés",
        "Se substituer aux professionnels de santé"
      ],
      right: [
        "Observer et noter des faits objectifs",
        "Partager ses observations en équipe",
        "Transmettre ses inquiétudes au responsable",
        "Accompagner l\u2019orientation vers les partenaires",
        "Continuer d\u2019accueillir l\u2019enfant avec bienveillance"
      ],
      narration: "Les limites du professionnel : ne pas diagnostiquer, ne pas alarmer sans fondement, ne pas décider seul. Il doit observer, partager en équipe, transmettre au responsable et accompagner l\u2019orientation."
    },
    // SLIDE 38 — Inclusion
    {
      type: 'content',
      title: "L\u2019inclusion : un droit fondamental",
      points: [
        "Basée sur les droits de l\u2019enfant : chaque enfant a droit à un accueil adapté",
        "L\u2019inclusion signifie accueillir TOUS les enfants, y compris ceux avec des besoins particuliers",
        "Égalité d\u2019accès : pas de discrimination liée au handicap, à la culture ou à la situation sociale",
        "Adaptation de l\u2019environnement et des pratiques, pas de l\u2019enfant",
        "L\u2019inclusion enrichit le groupe et favorise l\u2019apprentissage de la différence"
      ],
      highlight: "L\u2019inclusion n\u2019est pas \u00ab intégrer \u00bb l\u2019enfant dans un cadre rigide. C\u2019est adapter le cadre pour que chaque enfant y trouve sa place.",
      narration: "L\u2019inclusion est un droit fondamental. Chaque enfant a droit à un accueil adapté. L\u2019inclusion signifie adapter l\u2019environnement et les pratiques, pas l\u2019enfant."
    },
    // SLIDE 39 — Adaptation
    {
      type: 'grid',
      title: "Les dimensions de l\u2019adaptation",
      items: [
        { num: "1", label: "Environnement : espaces calmes, repères visuels, réduction des stimulations si nécessaire" },
        { num: "2", label: "Rythme : respecter le tempo de l\u2019enfant, ne pas forcer les transitions" },
        { num: "3", label: "Activités : adapter le matériel, les consignes, la durée et le niveau d\u2019exigence" },
        { num: "4", label: "Relation : individualiser la communication, être attentif aux signaux spécifiques" },
        { num: "5", label: "Équipe : travailler en coordination pour assurer la cohérence de l\u2019accompagnement" }
      ],
      narration: "Les dimensions de l\u2019adaptation : l\u2019environnement, le rythme, les activités, la relation et le travail en équipe."
    },
    // SLIDE 40 — Exemple inclusion
    {
      type: 'example',
      title: "Exemple — Enfant avec suspicion de TSA",
      situation: "Un enfant de 2 ans présente des comportements inhabituels : hypersensibilité sensorielle, retrait social, routines rigides.",
      observation: ["Se bouche les oreilles lors des moments de groupe bruyants", "Ne participe pas aux jeux avec les autres enfants", "S\u2019agite fortement lors des changements de routine", "Évite le regard et le contact physique"],
      analyse: ["Hypothèse de sensibilité sensorielle accrue", "Besoin de prévisibilité et de structure", "Difficulté dans les interactions sociales", "Nécessité d\u2019un accompagnement individualisé"],
      action: ["Proposer un espace calme accessible à tout moment", "Structurer la journée avec des repères visuels (photos, pictogrammes)", "Réduire les stimulations lors des moments de groupe", "Accompagnement individualisé pour les transitions", "Échanges réguliers avec les parents et orientation vers la PMI"],
      formulation: "\u00ab J\u2019observe que cet enfant est plus à l\u2019aise dans un environnement calme et structuré. En équipe, nous avons adapté l\u2019espace et les routines. Nous en parlons régulièrement avec les parents. \u00bb",
      narration: "Exemple : un enfant avec suspicion de troubles du spectre autistique. Adaptations : espace calme, repères visuels, réduction des stimulations, accompagnement individualisé et échanges avec les parents."
    },

    // ═══════════════ CHAPITRE 4 — COMMUNICATION ═══════════════
    {
      type: 'section',
      title: "CHAPITRE 4",
      subtitle: "Communication professionnelle avec les parents",
      chapterIcon: "users",
      chapterNum: "04",
      chapterColor: "#8B5CF6",
      narration: "Chapitre 4 : Communication professionnelle avec les parents. L\u2019objectif est de transmettre de manière professionnelle et éthique, en toutes circonstances."
    },
    // SLIDE 42 — Types de communication
    {
      type: 'multi',
      title: "Les types de communication",
      sections: [
        { title: "Communication verbale", items: ["Le choix des mots : précis, bienveillants, professionnels", "Le ton de voix : calme, posé, rassurant", "La reformulation pour vérifier la compréhension", "L\u2019adaptation du vocabulaire au niveau de l\u2019interlocuteur"] },
        { title: "Communication non verbale", items: ["Le regard : contact visuel chaleureux", "La posture : ouverte, à hauteur du parent", "Les gestes : accueillants, pas de bras croisés", "L\u2019expression du visage : congruente avec le message"] },
        { title: "Communication écrite", items: ["Cahier de liaison : transmissions quotidiennes", "Panneaux d\u2019affichage : informations pratiques", "Courriers : convocations, projets", "Photos : partage du quotidien de l\u2019enfant"] }
      ],
      narration: "Les trois types de communication : verbale avec le choix des mots et le ton, non verbale avec le regard et la posture, et écrite avec les cahiers de liaison et les panneaux d\u2019affichage."
    },
    // SLIDE 43 — Principes
    {
      type: 'grid',
      title: "Les principes de la communication professionnelle",
      items: [
        { num: "1", label: "Objectivité : s\u2019appuyer sur des faits observables, pas sur des interprétations" },
        { num: "2", label: "Clarté : messages simples, compréhensibles, sans jargon inutile" },
        { num: "3", label: "Bienveillance : ton respectueux, valorisation, absence de jugement" },
        { num: "4", label: "Confidentialité : ne pas divulguer d\u2019informations personnelles à des tiers" },
        { num: "5", label: "Régularité : communiquer de manière continue, pas seulement en cas de problème" }
      ],
      narration: "Les principes de la communication professionnelle : objectivité, clarté, bienveillance, confidentialité et régularité."
    },
    // SLIDE 44 — CNV
    {
      type: 'method',
      title: "La Communication Non Violente (CNV)",
      steps: [
        { label: "Observation", desc: "Décrire la situation de manière factuelle, sans jugement : \u00ab J\u2019ai observé que\u2026 \u00bb", icon: "eye" },
        { label: "Sentiment", desc: "Exprimer ce que la situation provoque : \u00ab Je me sens\u2026 / Ça m\u2019inquiète\u2026 \u00bb", icon: "heart" },
        { label: "Besoin", desc: "Identifier le besoin sous-jacent : \u00ab J\u2019ai besoin de comprendre\u2026 / L\u2019enfant a besoin de\u2026 \u00bb", icon: "brain" },
        { label: "Demande", desc: "Formuler une demande claire et concrète : \u00ab Pourriez-vous\u2026 ? / Que pensez-vous de\u2026 ? \u00bb", icon: "target" }
      ],
      quote: "La CNV permet de communiquer même dans les situations difficiles, en restant centré sur les faits, les émotions et les besoins.",
      narration: "La Communication Non Violente comporte quatre étapes : l\u2019observation factuelle, l\u2019expression du sentiment, l\u2019identification du besoin et la formulation d\u2019une demande claire."
    },
    // SLIDE 45 — Situations difficiles
    {
      type: 'grid',
      title: "Gestion des situations difficiles",
      items: [
        { num: "1", label: "Conflit : le parent est en désaccord avec une pratique. Écouter, reformuler, chercher une solution ensemble." },
        { num: "2", label: "Désaccord éducatif : le parent souhaite une pratique contraire au projet. Expliquer le cadre, chercher un compromis." },
        { num: "3", label: "Inquiétude du parent : le parent s\u2019inquiète pour son enfant. Rassurer avec des faits, proposer un échange approfondi." },
        { num: "4", label: "Agressivité : le parent est en colère. Rester calme, ne pas répondre à l\u2019agressivité, reformuler, proposer un temps de parole." },
        { num: "5", label: "Information sensible : transmettre un repérage délicat. Choisir le bon moment, le bon lieu, utiliser des faits objectifs." }
      ],
      narration: "Les situations difficiles incluent les conflits, les désaccords éducatifs, les inquiétudes, l\u2019agressivité et la transmission d\u2019informations sensibles. Dans tous les cas : écouter, reformuler, rester factuel."
    },
    // SLIDE 46 — Posture communication
    {
      type: 'content',
      title: "La posture de communication professionnelle",
      points: [
        "Écoute active : être pleinement présent et attentif",
        "Reformulation : montrer que l\u2019on a compris",
        "Distance professionnelle : ni trop proche, ni trop distant",
        "Maîtrise émotionnelle : ne pas se laisser envahir par ses propres émotions",
        "Confidentialité : ce qui est dit dans le cadre professionnel reste confidentiel"
      ],
      highlight: "La distance professionnelle n\u2019est pas de la froideur. C\u2019est la capacité à être bienveillant tout en restant dans son rôle professionnel.",
      narration: "La posture de communication professionnelle repose sur l\u2019écoute active, la reformulation, la distance professionnelle, la maîtrise émotionnelle et la confidentialité."
    },
    // SLIDE 47 — Exemple communication
    {
      type: 'example',
      title: "Exemple — Parent mécontent",
      situation: "Un parent arrive en colère car son enfant a été griffé par un autre enfant. Il élève la voix et accuse l\u2019équipe de ne pas surveiller.",
      observation: ["Parent visiblement en colère, voix forte", "Reproche direct : \u00ab Vous ne surveillez pas les enfants ! \u00bb", "Émotion de peur pour son enfant", "Besoin d\u2019être entendu et rassuré"],
      analyse: ["Réaction émotionnelle liée à l\u2019inquiétude parentale", "Besoin d\u2019explication et de réassurance", "Besoin que sa plainte soit prise au sérieux", "Le parent n\u2019attaque pas la personne mais exprime sa peur"],
      action: ["Accueillir l\u2019émotion sans se défendre : \u00ab Je comprends votre inquiétude \u00bb", "Reformuler : \u00ab Vous êtes en colère parce que votre enfant a été griffé et c\u2019est normal \u00bb", "Expliquer factuellement ce qui s\u2019est passé", "Présenter les mesures prises pour éviter que cela se reproduise", "Proposer un temps d\u2019échange plus calme si nécessaire"],
      formulation: "\u00ab Je comprends votre colère, c\u2019est normal de réagir quand votre enfant est blessé. Voici ce qui s\u2019est passé. Nous avons pris des mesures pour que cela ne se reproduise pas. Souhaitez-vous qu\u2019on en parle plus longuement ? \u00bb",
      narration: "Exemple : un parent mécontent après une griffure. Réponse : accueillir l\u2019émotion, reformuler, expliquer factuellement, présenter les mesures prises et proposer un temps d\u2019échange."
    },
    // SLIDE 48 — Attendu jury Ch4
    {
      type: 'content',
      title: "Attendu du jury — Chapitre 4",
      points: [
        "Montrer ta maîtrise émotionnelle face aux situations difficiles",
        "Démontrer ton professionnalisme dans la communication",
        "Utiliser la CNV ou des techniques similaires",
        "Savoir adapter ta communication selon l\u2019interlocuteur et le contexte",
        "Montrer que tu respectes la confidentialité"
      ],
      narration: "Le jury attend que tu montres ta maîtrise émotionnelle, ton professionnalisme dans la communication, l\u2019utilisation de la CNV et le respect de la confidentialité."
    },

    // ═══════════════ SYNTHÈSE ═══════════════
    {
      type: 'section',
      title: "SYNTHÈSE GÉNÉRALE",
      subtitle: "Ce que le jury attend du Bloc 3",
      chapterIcon: "star",
      chapterNum: "Fin",
      chapterColor: T.primary,
      narration: "Synthèse générale du Bloc 3. Voici ce que le jury attend de vous."
    },
    // SLIDE 50 — Compétences clés
    {
      type: 'grid',
      title: "Les 5 compétences clés du Bloc 3",
      items: [
        { num: "1", label: "Construire une alliance éducative durable avec les familles, basée sur la confiance et le respect" },
        { num: "2", label: "Adopter une posture non jugeante envers les parents et leurs choix éducatifs" },
        { num: "3", label: "Adapter sa communication à chaque interlocuteur et à chaque situation" },
        { num: "4", label: "Comprendre et accompagner les enjeux familiaux avec empathie et professionnalisme" },
        { num: "5", label: "Travailler en partenariat avec les familles, l\u2019équipe et les partenaires extérieurs" }
      ],
      narration: "Les cinq compétences clés du Bloc 3 : alliance éducative, posture non jugeante, communication adaptée, compréhension des enjeux familiaux et travail en partenariat."
    },
    // SLIDE 51 — Phrase type
    {
      type: 'content',
      title: "Phrase type à réutiliser au jury",
      points: [],
      quote: "\u00ab J\u2019accueille le parent dans une posture bienveillante, je prends en compte sa singularité et ses besoins, puis je co-construis avec lui un accompagnement adapté dans l\u2019intérêt de l\u2019enfant. \u00bb",
      highlight: "Cette phrase résume toute la démarche professionnelle du Bloc 3. Entraîne-toi à la formuler naturellement avec tes propres exemples.",
      narration: "La phrase type à réutiliser au jury : j\u2019accueille le parent dans une posture bienveillante, je prends en compte sa singularité et ses besoins, puis je co-construis avec lui un accompagnement adapté dans l\u2019intérêt de l\u2019enfant."
    },
    // SLIDE 52 — Erreurs fréquentes
    {
      type: 'posture',
      title: "Erreurs fréquentes à éviter dans le Bloc 3",
      wrong: [
        "Juger les parents ou leurs pratiques éducatives",
        "Se positionner en expert qui sait mieux que le parent",
        "Minimiser les émotions du parent lors de l\u2019accueil",
        "Diagnostiquer un trouble chez l\u2019enfant",
        "Rompre la confidentialité des échanges",
        "Répondre à l\u2019agressivité par l\u2019agressivité"
      ],
      right: [
        "Accueillir avec bienveillance et sans jugement",
        "Reconnaître le parent comme premier éducateur",
        "Valider les émotions et sécuriser la séparation",
        "Observer, transmettre et accompagner (pas diagnostiquer)",
        "Respecter la confidentialité en toute circonstance",
        "Utiliser la CNV pour gérer les situations difficiles"
      ],
      narration: "Erreurs fréquentes à éviter : juger, se positionner en expert, minimiser les émotions, diagnostiquer, rompre la confidentialité et répondre à l\u2019agressivité. Les bonnes pratiques : accueillir, reconnaître, valider, observer, respecter la confidentialité et utiliser la CNV."
    },
    // SLIDE 53 — Félicitations
    {
      type: 'congratulations',
      title: "Bloc 3 terminé !",
      message: "Tu maîtrises maintenant les fondamentaux de l\u2019accompagnement des parents et de l\u2019entourage des jeunes enfants.",
      details: [
        "Introduction : approche systémique et co-éducation",
        "Chapitre 1 : Accueillir et construire l\u2019alliance éducative",
        "Chapitre 2 : Accompagnement à la parentalité",
        "Chapitre 3 : Prévention précoce et inclusion",
        "Chapitre 4 : Communication professionnelle"
      ],
      narration: "Félicitations ! Tu as parcouru l\u2019ensemble du Bloc 3. Tu connais maintenant l\u2019approche systémique, l\u2019alliance éducative, l\u2019accompagnement à la parentalité, la prévention précoce et la communication professionnelle. Continue avec le quiz pour valider tes acquis !"
    }
  ];

  // ═══════════════ TTS ═══════════════
  const handleTextToSpeech = async () => {
    if (isPlaying) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setIsPlaying(false);
      return;
    }
    const slide = slides[currentSlide];
    let text = (slide.narration || slide.title || '').replace(/IEPE/g, 'I E P E').replace(/TP/g, 'titre professionnel').replace(/PAP/g, 'projet d\'accueil personnalisé').replace(/CNV/g, 'communication non violente').replace(/TSA/g, 'troubles du spectre autistique').replace(/PMI/g, 'protection maternelle et infantile');
    if (text.length > 800) text = text.substring(0, 800) + '...';
    if (!text.trim()) { toast.info("Pas de contenu audio pour cette slide"); return; }
    try {
      setIsLoading(true); setIsPlaying(true);
      const res = await fetch(`${window.location.origin}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, speed: 1.1 }) });
      if (!res.ok) throw new Error('TTS failed');
      const data = await res.json();
      const audioData = data.audio_base64 || data.audio;
      setIsLoading(false);
      if (audioData) {
        const blob = new Blob([Uint8Array.from(atob(audioData), c => c.charCodeAt(0))], { type: 'audio/mp3' });
        audioRef.current = new Audio(URL.createObjectURL(blob));
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => { toast.error("Erreur lecture audio"); setIsPlaying(false); };
        audioRef.current.play().catch(() => { toast.error("Impossible de lire l'audio"); setIsPlaying(false); });
      } else { toast.error("Pas de données audio"); setIsPlaying(false); }
    } catch { toast.error("Erreur génération audio"); setIsPlaying(false); setIsLoading(false); }
  };

  const nextSlide = () => { if (currentSlide < slides.length - 1) updateSlide(currentSlide + 1); };
  const prevSlide = () => { if (currentSlide > 0) updateSlide(currentSlide - 1); };

  // ═══════════════ RENDER HELPERS ═══════════════
  const ChapterIcon = ({ icon, size = 32 }) => {
    const props = { size, strokeWidth: 1.5 };
    if (icon === 'eye') return <Eye {...props} />;
    if (icon === 'heart') return <Heart {...props} />;
    if (icon === 'shield') return <Shield {...props} />;
    if (icon === 'users') return <Users {...props} />;
    if (icon === 'target') return <Target {...props} />;
    if (icon === 'brain') return <Brain {...props} />;
    if (icon === 'star') return <Star {...props} />;
    return <BookOpen {...props} />;
  };

  const renderSlide = () => {
    const slide = slides[currentSlide];

    if (slide.type === 'title') return (
      <div style={{ background: T.gradient, minHeight: '500px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30px', left: '-30px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', top: '60px', right: '40px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', bottom: '40px', left: '60px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <div style={{ position: 'absolute', bottom: '-20px', right: '-20px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ position: 'absolute', top: '20px', right: '80px', display: 'grid', gridTemplateColumns: 'repeat(5,8px)', gap: '8px', opacity: 0.15 }}>
          {Array.from({ length: 15 }).map((_, i) => <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'white' }} />)}
        </div>
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ width: '90px', height: '90px', borderRadius: '24px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <Handshake size={44} />
          </div>
          <div style={{ display: 'inline-block', padding: '0.3rem 1.2rem', borderRadius: '50px', background: 'rgba(255,255,255,0.15)', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}>
            TP IEPE — Bloc 3
          </div>
          <h1 style={{ fontSize: '3rem', fontWeight: 800, marginBottom: '1rem', lineHeight: 1.1 }}>{slide.title}</h1>
          <p style={{ fontSize: '1.35rem', maxWidth: '750px', opacity: 0.95, lineHeight: '1.6' }}>{slide.subtitle}</p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {['4 chapitres', `${slides.length} slides`, 'Audio inclus'].map((tag, i) => (
              <span key={i} style={{ padding: '0.35rem 0.85rem', borderRadius: '50px', background: 'rgba(255,255,255,0.12)', fontSize: '0.78rem', fontWeight: 500, border: '1px solid rgba(255,255,255,0.15)' }}>{tag}</span>
            ))}
          </div>
          <p style={{ marginTop: '1.25rem', fontSize: '0.85rem', opacity: 0.6 }}>Titre Professionnel Intervenant Éducatif Petite Enfance</p>
        </div>
      </div>
    );

    if (slide.type === 'sommaire') return (
      <div style={{ padding: '2rem', minHeight: '500px', background: `linear-gradient(180deg, #F8FAFF 0%, ${T.light} 100%)` }}>
        <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: P, marginBottom: '0.5rem', textAlign: 'center' }}>{slide.title}</h2>
        <p style={{ textAlign: 'center', color: '#64748B', fontSize: '0.85rem', marginBottom: '1.5rem' }}>Clique sur un chapitre pour y accéder directement</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          {slide.sections.map((s, i) => (
            <div key={i} onClick={() => { if (s.targetSlide !== undefined) updateSlide(s.targetSlide); }}
              style={{ background: 'white', borderRadius: '14px', border: `1px solid ${s.color}25`, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${s.color}20`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}>
              <div style={{ background: `${s.color}12`, padding: '0.75rem 1rem', borderBottom: `2px solid ${s.color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ background: s.color, color: 'white', width: '28px', height: '28px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>{s.num}</span>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: s.color }}>{s.title}</h3>
                </div>
              </div>
              <div style={{ padding: '0.75rem 1rem' }}>
                {s.items.map((item, j) => <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}><CheckCircle size={12} color={s.color} /><span style={{ fontSize: '0.82rem', color: '#4B5563' }}>{item}</span></div>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    if (slide.type === 'section') return (
      <div style={{ background: slide.chapterColor ? `linear-gradient(135deg, ${slide.chapterColor}DD 0%, ${slide.chapterColor} 100%)` : T.sectionGradient, minHeight: '500px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '30px', left: '30px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', bottom: '40px', right: '40px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        {slide.chapterNum && <div style={{ background: 'rgba(255,255,255,0.15)', width: '70px', height: '70px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.2)' }}><ChapterIcon icon={slide.chapterIcon} size={36} /></div>}
        {slide.chapterNum && <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.25rem 1rem', borderRadius: '50px', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '2px', marginBottom: '1rem' }}>{slide.chapterNum === 'Intro' ? 'INTRODUCTION' : slide.chapterNum === 'Fin' ? 'SYNTHÈSE' : `CHAPITRE ${slide.chapterNum}`}</div>}
        <h2 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '0.75rem' }}>{slide.title}</h2>
        {slide.subtitle && <p style={{ fontSize: '1.2rem', opacity: 0.92, maxWidth: '600px', lineHeight: 1.5, background: 'rgba(255,255,255,0.1)', padding: '0.75rem 1.5rem', borderRadius: '12px' }}>{slide.subtitle}</p>}
      </div>
    );

    if (slide.type === 'content') return (
      <div style={{ padding: '2rem', minHeight: '500px' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem' }}>{slide.title}</h2>
        {slide.points && slide.points.length > 0 && <div style={{ marginBottom: '1rem' }}>{slide.points.map((pt, i) => <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}><CheckCircle size={16} color={P} style={{ marginTop: '3px', flexShrink: 0 }} /><span style={{ fontSize: '0.95rem', color: '#374151', lineHeight: 1.6 }}>{pt}</span></div>)}</div>}
        {slide.highlight && <div style={{ background: `${P}10`, padding: '1rem', borderRadius: '10px', borderLeft: `4px solid ${P}`, marginBottom: '1rem' }}><p style={{ fontSize: '0.9rem', color: T.accent, lineHeight: 1.6, fontWeight: 500 }}>{slide.highlight}</p></div>}
        {slide.quote && <div style={{ background: '#F8FAFC', padding: '1.25rem', borderRadius: '10px', borderLeft: `4px solid ${T.secondary}`, marginTop: '1rem' }}><p style={{ fontSize: '0.95rem', fontStyle: 'italic', color: '#334155', lineHeight: 1.6 }}>{slide.quote}</p></div>}
      </div>
    );

    if (slide.type === 'grid') return (
      <div style={{ padding: '2rem', minHeight: '500px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem', textAlign: 'center' }}>{slide.title}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.75rem' }}>
          {slide.items.map((item, i) => <div key={i} style={{ background: 'white', borderRadius: '10px', padding: '0.75rem', border: `1px solid ${P}20`, display: 'flex', gap: '0.5rem', alignItems: 'flex-start', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}><span style={{ background: P, color: 'white', minWidth: '26px', height: '26px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>{item.num}</span><span style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.5 }}>{item.label}</span></div>)}
        </div>
      </div>
    );

    if (slide.type === 'multi') return (
      <div style={{ padding: '2rem', minHeight: '500px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem' }}>{slide.title}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem' }}>
          {slide.sections.map((s, i) => <div key={i} style={{ background: 'white', borderRadius: '10px', border: `1px solid ${P}25`, overflow: 'hidden' }}><div style={{ background: `${P}10`, padding: '0.6rem 0.75rem', borderBottom: `2px solid ${P}` }}><h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: T.accent }}>{s.title}</h3></div><div style={{ padding: '0.6rem 0.75rem' }}>{s.items.map((item, j) => <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.3rem' }}><CheckCircle size={12} color={P} style={{ marginTop: '3px', flexShrink: 0 }} /><span style={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>{item}</span></div>)}</div></div>)}
        </div>
      </div>
    );

    if (slide.type === 'method') return (
      <div style={{ padding: '2rem', minHeight: '500px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem', textAlign: 'center' }}>{slide.title}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {slide.steps.map((step, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'white', borderRadius: '12px', padding: '0.75rem 1rem', border: `1px solid ${P}20`, boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
            <div style={{ background: `${P}15`, width: '44px', height: '44px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ChapterIcon icon={step.icon} size={22} /></div>
            <div><strong style={{ color: P, fontSize: '0.95rem' }}>{i + 1}. {step.label}</strong><p style={{ fontSize: '0.85rem', color: '#6B7280', lineHeight: 1.5, marginTop: '0.15rem' }}>{step.desc}</p></div>
          </div>)}
        </div>
        {slide.quote && <div style={{ background: `${P}08`, padding: '1rem', borderRadius: '10px', borderLeft: `4px solid ${P}` }}><p style={{ fontSize: '0.9rem', fontStyle: 'italic', color: T.accent, lineHeight: 1.6 }}>{slide.quote}</p></div>}
      </div>
    );

    if (slide.type === 'example') return (
      <div style={{ padding: '2rem', minHeight: '500px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1rem' }}>{slide.title}</h2>
        <div style={{ background: T.light, padding: '0.75rem', borderRadius: '8px', borderLeft: `4px solid ${P}`, marginBottom: '0.75rem' }}><strong style={{ color: T.accent, fontSize: '0.85rem' }}>Situation : </strong><span style={{ fontSize: '0.85rem', color: '#374151' }}>{slide.situation}</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
          {slide.observation && <div style={{ background: '#EFF6FF', padding: '0.75rem', borderRadius: '8px', borderLeft: '4px solid #3B82F6' }}><strong style={{ color: '#1E40AF', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Observation</strong>{slide.observation.map((o, i) => <div key={i} style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.2rem' }}>- {o}</div>)}</div>}
          {slide.analyse && <div style={{ background: '#FDF4FF', padding: '0.75rem', borderRadius: '8px', borderLeft: '4px solid #A855F7' }}><strong style={{ color: '#7E22CE', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Analyse</strong>{slide.analyse.map((a, i) => <div key={i} style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.2rem' }}>- {a}</div>)}</div>}
        </div>
        {slide.action && <div style={{ background: '#F0FDF4', padding: '0.75rem', borderRadius: '8px', borderLeft: '4px solid #22C55E', marginBottom: '0.75rem' }}><strong style={{ color: '#16A34A', fontSize: '0.85rem', display: 'block', marginBottom: '0.4rem' }}>Action</strong>{slide.action.map((a, i) => <div key={i} style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.2rem' }}>- {a}</div>)}</div>}
        {slide.formulation && <div style={{ background: '#F8FAFC', padding: '0.75rem', borderRadius: '8px', borderLeft: `4px solid ${T.secondary}` }}><p style={{ fontSize: '0.88rem', fontStyle: 'italic', color: '#334155', lineHeight: 1.5 }}>{slide.formulation}</p></div>}
      </div>
    );

    if (slide.type === 'comparison') return (
      <div style={{ padding: '2rem', minHeight: '500px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem', textAlign: 'center' }}>{slide.title}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={{ background: T.light, borderRadius: '12px', padding: '1.25rem', border: `2px solid ${P}40` }}>
            <h3 style={{ color: P, fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.95rem' }}>{slide.left.title}</h3>
            {slide.left.items.map((item, i) => <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.4rem' }}><CheckCircle size={14} color={P} style={{ marginTop: '2px', flexShrink: 0 }} /><span style={{ fontSize: '0.85rem', color: '#1E3A8A', lineHeight: 1.5 }}>{item}</span></div>)}
          </div>
          <div style={{ background: '#F0FDF4', borderRadius: '12px', padding: '1.25rem', border: '2px solid #BBF7D0' }}>
            <h3 style={{ color: '#16A34A', fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.95rem' }}>{slide.right.title}</h3>
            {slide.right.items.map((item, i) => <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.4rem' }}><CheckCircle size={14} color="#22C55E" style={{ marginTop: '2px', flexShrink: 0 }} /><span style={{ fontSize: '0.85rem', color: '#166534', lineHeight: 1.5 }}>{item}</span></div>)}
          </div>
        </div>
      </div>
    );

    if (slide.type === 'posture') return (
      <div style={{ padding: '2rem', minHeight: '500px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: P, marginBottom: '1.25rem', textAlign: 'center' }}>{slide.title}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={{ background: '#FEF2F2', borderRadius: '12px', padding: '1.25rem', border: '2px solid #FECACA' }}>
            <h3 style={{ color: '#DC2626', fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.95rem' }}>Erreurs</h3>
            {slide.wrong.map((w, i) => <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.4rem' }}><AlertTriangle size={14} color="#EF4444" style={{ marginTop: '2px', flexShrink: 0 }} /><span style={{ fontSize: '0.85rem', color: '#991B1B', lineHeight: 1.5 }}>{w}</span></div>)}
          </div>
          <div style={{ background: '#F0FDF4', borderRadius: '12px', padding: '1.25rem', border: '2px solid #BBF7D0' }}>
            <h3 style={{ color: '#16A34A', fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.95rem' }}>Bonnes pratiques</h3>
            {slide.right.map((r, i) => <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.4rem' }}><CheckCircle size={14} color="#22C55E" style={{ marginTop: '2px', flexShrink: 0 }} /><span style={{ fontSize: '0.85rem', color: '#166534', lineHeight: 1.5 }}>{r}</span></div>)}
          </div>
        </div>
      </div>
    );

    if (slide.type === 'congratulations') return (
      <div style={{ background: T.gradient, minHeight: '500px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'white', textAlign: 'center' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}><Star size={40} /></div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>{slide.title}</h1>
        <p style={{ fontSize: '1.2rem', maxWidth: '600px', marginBottom: '2rem', opacity: 0.95 }}>{slide.message}</p>
        <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1.5rem', borderRadius: '16px', maxWidth: '500px', width: '100%', border: '1px solid rgba(255,255,255,0.15)' }}>
          {slide.details.map((d, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}><CheckCircle size={16} /><span style={{ fontSize: '0.9rem' }}>{d}</span></div>)}
        </div>
      </div>
    );

    return <div style={{ padding: '2rem', textAlign: 'center' }}><p>Type de slide non reconnu : {slide.type}</p></div>;
  };

  // ═══════════════ CHAPTER PROGRESS BAR ═══════════════
  const chapters = [
    { label: 'Intro', start: 0, end: 11, color: T.primary },
    { label: 'Ch.1', start: 12, end: 21, color: '#E75480' },
    { label: 'Ch.2', start: 22, end: 31, color: '#D97706' },
    { label: 'Ch.3', start: 32, end: 40, color: '#059669' },
    { label: 'Ch.4', start: 41, end: 48, color: '#8B5CF6' },
    { label: 'Fin', start: 49, end: slides.length - 1, color: T.primary }
  ];

  return (
    <div data-testid="tp-iepe-bloc3-presentation" style={{ maxWidth: '900px', margin: '0 auto', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', background: 'white' }}>
      {showResume && (
        <div style={{ background: T.light, padding: '0.75rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `2px solid ${P}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: P }}><RotateCcw size={16} /><span style={{ fontSize: '0.9rem' }}>Tu t'es arrêté(e) à la slide {savedSlide + 1}/{slides.length}</span></div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => { updateSlide(savedSlide); setShowResume(false); }} style={{ padding: '0.4rem 1rem', background: P, color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}>Reprendre</button>
            <button onClick={() => { updateSlide(0); setShowResume(false); }} style={{ padding: '0.4rem 1rem', background: 'white', color: P, border: `1px solid ${P}`, borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}>Recommencer</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 1rem', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', overflowX: 'auto' }}>
        {chapters.map((ch, i) => {
          const active = currentSlide >= ch.start && currentSlide <= ch.end;
          const done = currentSlide > ch.end;
          return (
            <React.Fragment key={i}>
              {i > 0 && <div style={{ width: '20px', height: '2px', background: done ? ch.color : '#D1D5DB', flexShrink: 0 }} />}
              <button onClick={() => updateSlide(ch.start)} style={{ padding: '0.3rem 0.6rem', borderRadius: '50px', border: active ? `2px solid ${ch.color}` : '1px solid #D1D5DB', background: done ? `${ch.color}15` : active ? `${ch.color}10` : 'white', color: active || done ? ch.color : '#9CA3AF', fontSize: '0.7rem', fontWeight: active ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>{ch.label}</button>
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ height: '4px', background: '#e2e8f0' }}><div style={{ height: '100%', background: T.gradient, width: `${((currentSlide + 1) / slides.length) * 100}%`, transition: 'width 0.3s ease' }} /></div>
      {renderSlide()}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB', background: '#FAFAFA' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: currentSlide === 0 ? '#e2e8f0' : 'white', color: currentSlide === 0 ? '#94a3b8' : P, border: `1px solid ${currentSlide === 0 ? '#e2e8f0' : P}`, borderRadius: '0.5rem', cursor: currentSlide === 0 ? 'not-allowed' : 'pointer', fontWeight: '500' }}><ChevronLeft size={20} /> Précédent</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={handleTextToSpeech} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', background: isPlaying ? P : `${P}10`, color: isPlaying ? 'white' : P, border: `1px solid ${P}30`, cursor: 'pointer' }}>
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : isPlaying ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <span style={{ padding: '0.5rem 1rem', background: P, color: 'white', borderRadius: '1rem', fontSize: '0.875rem', fontWeight: '500' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: currentSlide === slides.length - 1 ? '#e2e8f0' : P, color: currentSlide === slides.length - 1 ? '#94a3b8' : 'white', border: 'none', borderRadius: '0.5rem', cursor: currentSlide === slides.length - 1 ? 'not-allowed' : 'pointer', fontWeight: '500' }}>Suivant <ChevronRight size={20} /></button>
      </div>
    </div>
  );
};

export default TPIEPEBloc3Presentation;
